/* eslint-disable @typescript-eslint/no-explicit-any */
declare let google: any
declare let window: any
import { useRef, useCallback } from 'react'
import { useScript } from 'usehooks-ts'
import type { Status } from 'usehooks-ts'
import { useEffect, useState } from 'react'
import { useCurrentReceptor } from 'src/context/FormContext/CurrentReceptorContext'
import { useModalState } from 'src/Modal/ModalStateContext'
import { useGetLinker } from 'src/pages/Receptors/receptors.hooks'
import { getReceptorTemplate } from 'src/xgenerated/receptor'
import { ModalFormId } from 'src/components/ModalForms'
import { LINKER_MODEL_IDS } from '../../Linkers.constants'
import { useGoogleDriveContext } from '../../context/GoogleDriveContext'
import {
  PickerConfiguration,
  PickerResponse,
  GOOGLE_PICKER_SUPPORTED_FILE_TYPES,
} from './GoogleDriveWizard.constants'

export const useLoadGoogleApi = (shouldLoad = true): boolean => {
  const status: Status = useScript('https://apis.google.com/js/api.js')
  const [ready, setReady] = useState<boolean>(false)

  useEffect(() => {
    const onApiLoad = () => {
      setReady(true)
    }
    const loadApis = () => {
      window.gapi.load('client:auth2', () => null)
      window.gapi.load('picker', onApiLoad)
    }
    if (status === 'ready' && !ready && shouldLoad) {
      loadApis()
    }
  }, [status, ready, shouldLoad])

  return ready
}

export const useGooglePicker = (): ((
  config: PickerConfiguration,
) => Promise<PickerResponse>) => {
  const { setFilesSelected } = useGoogleDriveContext()
  const showPicker = useRef<boolean>(false)
  const ready = useLoadGoogleApi(!showPicker.current)
  const { setReceptor } = useCurrentReceptor()
  const { openModal } = useModalState()
  const getLinker = useGetLinker()

  const createPicker = useCallback(
    async (config: PickerConfiguration) => {
      const callback = async (tokendata: {
        action: string
        docs: google.picker.DocumentObject[]
      }) => {
        if (tokendata.action === google.picker.Action.PICKED) {
          setFilesSelected?.(tokendata.docs)
          openModal(config.modalId)
        } else if (tokendata.action === google.picker.Action.CANCEL) {
          setFilesSelected?.([])
        }
        showPicker.current = false
      }
      const linker = await getLinker(LINKER_MODEL_IDS.GOOGLE_DRIVE)
      if (!linker?.getCredentials().length) {
        const receptor = getReceptorTemplate(LINKER_MODEL_IDS.GOOGLE_DRIVE)
        setReceptor(receptor)
        openModal(ModalFormId.CONNECT_LINKER)
        return {
          isShown: false,
          invalidCredentials: false,
          exceptions: '',
        }
      }
      if (linker?.getExceptions().length > 0) {
        return {
          isShown: false,
          invalidCredentials: false,
          exceptions: linker.getExceptions(),
        }
      }
      if (!linker?.getIscredvalid()) {
        return {
          isShown: false,
          invalidCredentials: true,
          exceptions: '',
        }
      }
      const creds = JSON.parse(linker?.getCredentials())

      const token = creds.token.access_token

      const view = new google.picker.DocsView(config.viewId)
        .setIncludeFolders(true)
        .setMimeTypes(GOOGLE_PICKER_SUPPORTED_FILE_TYPES)
        .setSelectFolderEnabled(config.selectFolderEnabled || false)

      const driveView = new google.picker.DocsView(config.viewId)
        .setIncludeFolders(true)
        .setMimeTypes(GOOGLE_PICKER_SUPPORTED_FILE_TYPES)
        .setSelectFolderEnabled(config.selectFolderEnabled || false)
        .setEnableDrives(true)

      const picker = new google.picker.PickerBuilder()
        .setOAuthToken(token)
        .setDeveloperKey(process.env.REACT_APP_GOOGLE_API_KEY || '')
        .setLocale('en')
        .addView(view)
        .addView(driveView)
        .setOrigin(window.location.origin)
        .setTitle('Select a file from Google Drive or Shared Drives')
        .setCallback(callback)
      config.allowMultiselect &&
        picker.enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      const pickerBuilder = picker.build()
      pickerBuilder.setVisible(true)

      return {
        isShown: true,
        invalidCredentials: false,
        exceptions: '',
      }
    },
    [getLinker, openModal, setReceptor, setFilesSelected],
  )
  // open the picker
  const openPicker = useCallback(
    async (config: PickerConfiguration) => {
      // if we have token and everything is loaded open the picker
      if (ready && !showPicker.current) {
        showPicker.current = true
        return createPicker(config)
      }
      return {
        isShown: false,
        invalidCredentials: false,
        exceptions: '',
      }
    },
    [ready, createPicker],
  )

  return openPicker
}
